import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Empty, Select, Spin, Tag } from "antd";
import { CloseOutlined, PaperClipOutlined, PlusOutlined } from "@ant-design/icons";

import { searchFtcReferences } from "./controller";

/**
 * Attach Smilepass records to an outgoing email.
 *
 * The FTC picks a breakdown, estimate, patient, policy or request; the server
 * renders each one into a readable card the clinic sees without logging in, plus
 * a deep link for those who can. An estimate also carries its stored PDF, so the
 * FTC never downloads and re-uploads a file Smilepass is already holding.
 *
 * The list here comes from the server already scoped to the chosen office, and
 * the server re-checks every id at send time. This picker is convenience; it is
 * not the boundary. Do not add a "paste an id" affordance.
 */

const KINDS = [
  { value: "breakdown", label: "Coverage breakdown" },
  { value: "estimate", label: "Treatment estimate" },
  { value: "request", label: "Verification request" },
  { value: "policy", label: "Insurance policy" },
  { value: "patient", label: "Patient" },
];

const KIND_LABEL = KINDS.reduce((acc, k) => Object.assign(acc, { [k.value]: k.label }), {});

/** One line of "what am I looking at" — patient first, because that is how an FTC thinks. */
function describe(snapshot) {
  const bits = [];
  if (snapshot.patient) bits.push(snapshot.patient);
  if (snapshot.carrier) bits.push(snapshot.carrier);
  if (snapshot.member_id) bits.push(`#${snapshot.member_id}`);
  if (snapshot.created) bits.push(snapshot.created);
  return bits.join(" · ");
}

function refKey(ref) {
  return `${ref.kind}:${ref.id}`;
}

export default function ReferencePicker({ officeId, value, onChange, disabled }) {
  const [kind, setKind] = useState("breakdown");
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const selected = value || [];
  // Every keystroke fires a search; only the newest reply may paint. Without this
  // a slow early request can land after a fast later one and show stale rows.
  const latest = useRef(0);

  const search = useCallback(
    async function (nextKind, term) {
      if (!officeId) return;
      const ticket = ++latest.current;
      setLoading(true);
      const res = await searchFtcReferences(officeId, nextKind, term);
      if (ticket !== latest.current) return;
      setOptions(((res.json && res.json.results) || []));
      setLoading(false);
    },
    [officeId],
  );

  useEffect(
    function () {
      if (!open || !officeId) return undefined;
      const t = setTimeout(() => search(kind, q), q ? 300 : 0);
      return function () { clearTimeout(t); };
    },
    [open, officeId, kind, q, search],
  );

  // A different clinic means different records; anything already picked belongs to
  // the old one and the server would refuse it anyway.
  useEffect(
    function () {
      setOptions([]);
      setQ("");
    },
    [officeId],
  );

  function add(ref) {
    if (selected.some((s) => refKey(s) === refKey(ref))) return;
    onChange(selected.concat([ref]));
    setOpen(false);
    setQ("");
  }

  function remove(ref) {
    onChange(selected.filter((s) => refKey(s) !== refKey(ref)));
  }

  return (
    <div className="ftcref">
      <div className="ftcref-controls">
        <Select
          value={kind}
          onChange={(v) => { setKind(v); setOptions([]); }}
          options={KINDS}
          disabled={disabled || !officeId}
          style={{ width: 190 }}
        />
        <Select
          showSearch
          open={open}
          onDropdownVisibleChange={setOpen}
          value={null}
          searchValue={q}
          onSearch={setQ}
          filterOption={false}
          disabled={disabled || !officeId}
          placeholder={officeId ? "Search by patient name…" : "Choose a clinic first"}
          notFoundContent={
            loading ? <Spin size="small" /> : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                     description={q ? "Nothing matches." : "No records yet."} />
            )
          }
          onSelect={(_v, option) => add(option.ref)}
          options={options.map((r) => ({
            value: refKey(r),
            ref: r,
            label: (
              <span className="ftcref-option">
                <span className="ftcref-option-main">{describe(r.snapshot) || `#${r.id}`}</span>
                {r.snapshot.has_document ? <PaperClipOutlined /> : null}
              </span>
            ),
          }))}
          suffixIcon={<PlusOutlined />}
          style={{ flex: 1, minWidth: 220 }}
        />
      </div>

      {selected.length ? (
        <div className="ftcref-chosen">
          {selected.map((r) => (
            <div className="ftcref-chip" key={refKey(r)}>
              <Tag className="ftcref-chip-kind">{KIND_LABEL[r.kind] || r.kind}</Tag>
              <span className="ftcref-chip-text">{describe(r.snapshot) || `#${r.id}`}</span>
              {r.snapshot && r.snapshot.has_document ? (
                <PaperClipOutlined className="ftcref-chip-clip" title="Its PDF will be attached" />
              ) : null}
              <Button type="text" size="small" icon={<CloseOutlined />}
                      disabled={disabled} onClick={() => remove(r)} />
            </div>
          ))}
          <p className="ftcref-note">
            Each appears in the email as a summary card the clinic can read without
            signing in.
          </p>
        </div>
      ) : null}
    </div>
  );
}
